import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Slide,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import managerClassApi from "api/managerClass";
import { useAppDispatch } from "app/hooks";
import InputField from "components/FormFields/InputField";
import { ResponseStatus } from "models/common";
import { IClass } from "models/managerClass";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchClassList, selectFilterClassList } from "../../managerClassSlice";

interface EditClassProps {
  valueClass: IClass;
  openModal: boolean;
  setOpenModal: (openModal: boolean) => void;
}

export default function EditClass({ valueClass, openModal, setOpenModal }: EditClassProps) {
  const useStyles = makeStyles(() => ({
    modalContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(15, 23, 42, 0.5)",
    },
    modalCard: {
      width: 540,
      maxWidth: "92vw",
      borderRadius: 24,
      backgroundColor: "#FFFFFF",
      boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
      border: "1px solid #E2E8F0",
      outline: "none",
      overflow: "hidden",
    },
    modalHeader: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      color: "#FFFFFF",
      padding: "18px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    modalBody: {
      padding: "24px 28px",
    },
    actionGroup: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 12,
      marginTop: 24,
    },
    btnCancel: {
      borderRadius: 10,
      padding: "9px 24px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      color: "#475569",
      borderColor: "#CBD5E1",
      "&:hover": {
        backgroundColor: "#F8FAFC",
        borderColor: "#94A3B8",
      },
    },
    btnSubmit: {
      background: "linear-gradient(135deg, #0F172A 0%, #312E81 100%)",
      borderRadius: 10,
      padding: "9px 28px",
      fontSize: "0.875rem",
      fontWeight: 700,
      textTransform: "none",
      color: "#FFFFFF !important",
      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.2)",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "linear-gradient(135deg, #1E1B4B 0%, #4338CA 100%)",
        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.3)",
      },
    },
  }));

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterClassList = useSelector(selectFilterClassList);

  const initialValues = useMemo(() => ({
    name: valueClass?.name || "",
    description: valueClass?.description || "",
  }), [valueClass?.name, valueClass?.description]);

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
  });

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleSubmitForm = async (value: any) => {
    try {
      const res: ResponseStatus = await managerClassApi.updateClass(value, valueClass?.id);
      if (res.responseCode === "00") {
        toast.success("Cập nhật lớp học thành công");
        dispatch(fetchClassList(filterClassList));
        setOpenModal(false);
      } else {
        toast.error(`${t("managerClass.capnhatthatbai")}`);
        setOpenModal(false);
        throw new Error(`${t("managerClass.capnhatthatbai")}`);
      }
    } catch (error) { }
  };

  return (
    <Modal
      open={openModal}
      onClose={handleClose}
      className={classes.modalContainer}
      closeAfterTransition
    >
      <Slide direction="down" in={openModal} mountOnEnter unmountOnExit>
        <Paper className={classes.modalCard} elevation={0}>
          {/* Header matching CreateStudent */}
          <Box className={classes.modalHeader}>
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <EditIcon style={{ color: "#818CF8", fontSize: 24 }} />
              <Typography variant="h6" style={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.125rem" }}>
                {t("managerClass.capnhatclass")}
              </Typography>
            </Box>
            <IconButton onClick={handleClose} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Form */}
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Box className={classes.modalBody}>
              <InputField
                name="name"
                control={control}
                label={t("managerClass.tenclass") + " *"}
                size="small"
                borderRadius="12px"
                required={true}
              />

              <Box mt={2}>
                <InputField
                  multiline
                  rows={3}
                  name="description"
                  control={control}
                  label="Thông tin / Mô tả lớp học"
                  size="small"
                  borderRadius="12px"
                />
              </Box>

              {/* Actions */}
              <Box className={classes.actionGroup}>
                <Button
                  variant="outlined"
                  className={classes.btnCancel}
                  onClick={handleClose}
                >
                  {t("button.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.btnSubmit}
                >
                  {t("button.update")}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Slide>
    </Modal>
  );
}
